import type { Catalog, Family, Service, ServiceGroup } from './catalog.js'
import { computedServiceLabel } from './catalogDerive.js'

// Recherche du catalogue. Fonctions pures, sans dépendance : elles vivent ici
// pour la même raison que catalogDerive.ts — le champ `keywords` qu'elles
// consomment est un contrat, et la façon de le lire ne doit pas se réinventer
// dans chaque interface qui cherchera un jour dans le catalogue.
//
// Le comportement d'origine comparait la requête entière en sous-chaîne du
// libellé (`normalize(label).includes(query)`). Trois conséquences mesurées
// dans l'issue #97 : « épilation maillot » ne rendait rien (les deux mots ne
// sont jamais contigus dans cet ordre), « Rituel » et « Minceur » non plus
// (les accroches de famille n'étaient pas indexées), et aucun mot du langage
// courant ne rejoignait le vocabulaire métier du catalogue.

/**
 * Réduit un texte à sa forme comparable : minuscules, sans accents, sans
 * ponctuation.
 *
 * Les séparateurs deviennent des ESPACES et ne disparaissent pas : sinon
 * « semi-permanent » se recolle en « semipermanent » et ne correspond plus à
 * « Semi permanents », écrit en deux mots dans le catalogue. Les chiffres sont
 * conservés — « 3/4 jambes » et « SPA Mini Kids (7 à 13ans) » se cherchent.
 */
export function normalizeSearchText(input: string): string {
  return String(input ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

/**
 * Mots vides du français. Ils sont écartés de la requête parce qu'ils ne
 * discriminent rien et qu'exiger leur présence fait perdre des résultats
 * justes : « soin du visage » ne rend les soins visage que si « du » figure
 * quelque part dans le texte indexé, ce qui ne tient que par accident —
 * « épilation du maillot » n'a pas cette chance.
 *
 * Ils sont écartés de la REQUÊTE seulement, jamais du texte indexé : les
 * retirer des deux côtés reviendrait au même résultat pour un coût inutile,
 * et les retirer du seul texte indexé casserait les recherches qui les
 * contiennent légitimement.
 */
const MOTS_OUTILS = new Set([
  'a', 'au', 'aux', 'avec', 'dans', 'de', 'des', 'du', 'en', 'et', 'l', 'la',
  'le', 'les', 'ou', 'par', 'pour', 'sans', 'sur', 'un', 'une',
])

/**
 * Longueur minimale d'un radical. En dessous, le pluriel est laissé tel quel :
 * « dos » réduit à « do » correspondrait à « douceurs », et « massage dos »
 * remonterait des séances de hammam.
 */
const LONGUEUR_MINIMALE_DU_RADICAL = 4

/**
 * Radical d'un mot au pluriel — « massages » → « massage », « chevaux » laissé
 * tel quel faute d'être un pluriel en -s/-x régulier au sens de cette règle.
 *
 * Un seul sens est traité, et c'est suffisant : la correspondance se fait en
 * sous-chaîne, donc une requête au singulier trouve déjà un catalogue au
 * pluriel (« massage » est contenu dans « massages »). Seul le sens inverse —
 * requête au pluriel, catalogue au singulier — demandait ce traitement.
 */
function radical(mot: string): string {
  if (!/[sx]$/.test(mot)) return mot
  const stem = mot.slice(0, -1)
  return stem.length >= LONGUEUR_MINIMALE_DU_RADICAL ? stem : mot
}

/**
 * Mots retenus d'une requête, chacun réduit à son radical. Chercher « ÉPILATION
 * Maillot » et « maillot epilation » produit la même liste, à l'ordre près —
 * et l'ordre n'entre pas dans la correspondance.
 */
export function searchTokens(query: string): string[] {
  const bruts = normalizeSearchText(query).split(' ').filter(Boolean)
  const utiles = bruts.filter((mot) => !MOTS_OUTILS.has(mot))
  // Une requête faite UNIQUEMENT de mots outils (« de », « et ») n'est pas une
  // requête vide : on la cherche telle quelle, ce qui rend « aucun résultat ».
  // La replier sur la liste vide ferait tout remonter, ce qui serait faux.
  return (utiles.length > 0 ? utiles : bruts).map(radical)
}

export type SearchEntry = {
  family: Family
  group: ServiceGroup
  service: Service
  /** Libellé affiché de la prestation, déjà calculé pour le rendu du résultat. */
  label: string
  /** Texte normalisé dans lequel chaque mot de la requête est cherché. */
  haystack: string
}

/**
 * Texte indexé d'une prestation : tout ce qui la désigne, du plus précis au
 * plus général, plus les mots-clés des trois niveaux.
 *
 * La durée en fait partie parce qu'elle est AFFICHÉE à côté du libellé, dans
 * la liste comme dans les résultats (« Soin relaxant · 30 min ») : ce que la
 * visiteuse lit à l'écran doit se chercher. L'accroche de la famille y est
 * pour la même raison — elle est rédigée pour être lue, elle est sur la carte
 * de chaque famille, et c'est elle qui porte « rituels » et « minceur ».
 */
function texteIndexe(family: Family, group: ServiceGroup, service: Service, label: string): string {
  return normalizeSearchText(
    [
      label,
      service.duration ?? '',
      ...service.keywords,
      group.name,
      ...group.keywords,
      family.name,
      family.tagline,
      ...family.keywords,
    ].join(' '),
  )
}

/**
 * Aplatit le catalogue en un index de recherche. À mémoriser sur le catalogue
 * par l'appelant : la normalisation de ~150 prestations n'a pas à être refaite
 * à chaque frappe.
 */
export function buildSearchIndex(catalog: Catalog): SearchEntry[] {
  return catalog.flatMap((family) =>
    family.groups.flatMap((group) =>
      group.services.map((service) => {
        const label = computedServiceLabel(service)
        return { family, group, service, label, haystack: texteIndexe(family, group, service, label) }
      }),
    ),
  )
}

/**
 * Prestations dont le texte indexé contient CHACUN des mots de la requête, dans
 * n'importe quel ordre. C'est la règle qui débloque les requêtes à plusieurs
 * mots : elles ne demandent plus que les mots soient contigus.
 *
 * Chaque mot est cherché en sous-chaîne et non en mot entier, pour que
 * « vernis semi permanent » trouve « Semi permanents » et que « épil » trouve
 * « épilation » pendant la frappe.
 */
export function searchCatalogIndex(index: SearchEntry[], query: string): SearchEntry[] {
  const mots = searchTokens(query)
  if (mots.length === 0) return []
  return index.filter((entry) => mots.every((mot) => entry.haystack.includes(mot)))
}

/** Raccourci pour un appel ponctuel — un test, un script. Une interface qui
 * cherche à chaque frappe passe par buildSearchIndex + searchCatalogIndex. */
export function searchCatalog(catalog: Catalog, query: string): SearchEntry[] {
  return searchCatalogIndex(buildSearchIndex(catalog), query)
}
